/**
 * `dnd.ts`, le glisser-deposer a poignee du socle : le formulaire de brief du
 * Studio, et les tableaux du kit Parametres (`SettingsTable`).
 *
 * Porte du glisser-deposer generique de V1 (`launcher/index.html`, le bloc
 * `DRAG` / `dndArr` / `dndClear`) : deux genres, `champ` (les lignes du tableau
 * de champs, empilees verticalement) et `carte` (les cartes de la fiche, qui
 * peuvent etre cote a cote, d'ou l'axe X de V1).
 *
 * LA FRONTIERE, tenue ici : ce module ne connait PAS le modele. Il recoit un
 * ORDRE d'`uid` et rend un ordre d'`uid`. C'est l'appelant qui ecrit dans le
 * brief (ou la preference). Aucun import du modele.
 *
 * DEUX ELEMENTS, comme en V1, et ce n'est pas un detail : la POIGNEE porte le
 * glisser, la LIGNE recoit le depot. Une ligne entierement `draggable` volerait
 * la selection de texte dans les `<input>` qu'elle contient, et le fondateur ne
 * pourrait plus corriger un mot au milieu d'un libelle. D'ou la forme de
 * `propsDe`, qui rend les deux jeux de props d'un coup.
 *
 * LE CLAVIER, seul ecart avec V1 : V1 n'avait que la souris, donc un champ ne se
 * reordonnait pas au clavier du tout. Ici la poignee est focalisable. Espace ou
 * Entree la SAISIT, les fleches la deplacent d'un cran, Espace ou Entree la
 * DEPOSENT, Echap remet l'ordre d'avant la saisie.
 */

import type { DragEvent, FocusEvent, KeyboardEvent } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Les deux genres de V1 : les lignes de champs, et les cartes de la fiche. */
export type GenreDnd = 'champ' | 'carte';

/**
 * L'uid de la zone de depot de FIN de liste, reprise de V1 (`rid === '__end'`).
 * Une cible qui porte cet uid envoie l'element deplace en derniere position, ce
 * qui permet de le poser apres le dernier element sans viser sa moitie basse.
 */
export const FIN_DE_LISTE = '__end';

/** Ou l'element tombera, par rapport a la cible survolee. */
export type CoteDepot = 'avant' | 'apres';

/**
 * Les props de la POIGNEE : c'est elle qu'on saisit, a la souris ou au clavier.
 *
 * A etaler sur un `<button type="button">`. Volontairement SANS `className` ni
 * `style` : l'habillage vient de `@taki/ui` et des classes du composant.
 */
export interface PropsPoignee {
  draggable: boolean;
  tabIndex: number;
  'aria-roledescription': string;
  'aria-grabbed': boolean;
  'aria-label'?: string;
  /** `saisi` quand cet element est en cours de deplacement, absent sinon. */
  'data-dnd-etat'?: 'saisi';
  onDragStart(e: DragEvent<HTMLElement>): void;
  onDragEnd(): void;
  onKeyDown(e: KeyboardEvent<HTMLElement>): void;
  onBlur(e: FocusEvent<HTMLElement>): void;
}

/**
 * Les props de la LIGNE (ou de la carte) : la cible de depot.
 *
 * L'etat se lit sur les attributs `data-*`, que Tailwind sait cibler
 * (`data-[dnd-depot=avant]:border-t-2`, `data-[dnd-etat=saisi]:opacity-50`).
 *
 * L'axe de comparaison vient du GENRE : une carte se pose a gauche ou a droite
 * d'une autre, un champ au-dessus ou en-dessous. Pour forcer l'axe sur un
 * element precis (une carte en pleine largeur, qui prend sa rangee entiere),
 * poser `data-axis="y"` dessus : il l'emporte, comme le `data-axis` de V1.
 */
export interface PropsLigne {
  'data-dnd-genre': GenreDnd;
  'data-dnd-uid': string;
  'data-dnd-etat'?: 'saisi';
  /** `avant` ou `apres` sur la cible survolee : c'est l'indicateur de depot. */
  'data-dnd-depot'?: CoteDepot;
  onDragOver(e: DragEvent<HTMLElement>): void;
  onDragLeave(e: DragEvent<HTMLElement>): void;
  onDrop(e: DragEvent<HTMLElement>): void;
}

/**
 * CE QUE `propsDe` REND : DEUX JEUX SEPARES, et la separation est le garde-fou.
 *
 * Il rendait un seul objet (`PropsDnd extends PropsLigne`, avec `poignee`
 * dedans), et les deux appelants l'ont etale TEL QUEL sur leur ligne : `poignee`
 * partait alors en attribut de DOM, et la ⠿ ne gardait qu'un `draggable` nu,
 * sans `onDragStart`. Rien n'appelait `setEnCours`, donc chaque `onDragOver` et
 * chaque `onDrop` sortaient au premier test : on attrapait un champ ou une
 * carte, ils ne tombaient nulle part, sur un ecran vert et un typecheck vert
 * (TypeScript ne verifie pas les proprietes en trop d'un etalement).
 *
 * Deux clefs nommees ne s'etalent pas par accident : il faut choisir laquelle
 * va ou, ce qui est justement la seule chose a savoir.
 */
export interface PropsDnd {
  /** Ce qui RECOIT le depot : a etaler sur la ligne, la carte, la rangee. */
  ligne: PropsLigne;
  /** Ce qui porte le GLISSER : a etaler sur un `<button type="button">`. */
  poignee: PropsPoignee;
}

export interface Dnd {
  /**
   * Les props de l'element portant cet `uid`.
   *
   * ```tsx
   * const { ligne, poignee } = dnd.propsDe(champ.uid);
   * <tr key={champ.uid} {...ligne} draggable={false}>
   *   <td><button type="button" {...poignee}>⠿</button></td>
   * ```
   *
   * Une zone de depot qui n'est pas elle-meme deplacable (la butee de fin) ne
   * prend que `ligne` et laisse `poignee` de cote. Un element qui se saisit
   * ENTIEREMENT (une carte de l'apercu) prend les deux.
   */
  propsDe(uid: string): PropsDnd;
  /** L'uid en cours de deplacement (souris ou clavier), sinon `null`. */
  enCours: string | null;
  /** Message court a rendre dans une zone vive pour le geste clavier. */
  annonce: string;
}

/* ========================================================================== */
/* le calcul d'ordre, pur                                                     */
/* ========================================================================== */

/**
 * Deplace `uid` juste avant ou juste apres `cible`, et rend l'ordre resultant.
 *
 * Pur : le tableau d'entree n'est jamais touche. Rend le tableau d'ENTREE
 * lui-meme quand rien ne bouge, ce qui permet a l'appelant de ne pas ecrire dans
 * le brief pour rien (donc de ne pas re-rendre le formulaire pour rien).
 *
 * `cible` valant `FIN_DE_LISTE` envoie l'element en derniere position, comme le
 * `rid === '__end'` de V1.
 */
export function ordreApresDepot(ordre: string[], uid: string, cible: string, cote: CoteDepot): string[] {
  const depart = ordre.indexOf(uid);
  if (depart < 0) return ordre;

  if (cible === FIN_DE_LISTE) {
    if (depart === ordre.length - 1) return ordre;
    const suite = ordre.slice();
    suite.splice(depart, 1);
    suite.push(uid);
    return suite;
  }

  if (cible === uid) return ordre;
  const suite = ordre.slice();
  suite.splice(depart, 1);
  const arrivee = suite.indexOf(cible);
  if (arrivee < 0) return ordre;
  const place = cote === 'apres' ? arrivee + 1 : arrivee;
  if (place === depart) return ordre;
  suite.splice(place, 0, uid);
  return suite;
}

/**
 * Deplace `uid` de `pas` crans (negatif vers le debut, positif vers la fin) :
 * le geste du clavier. Rend l'ordre d'entree quand le bord est atteint.
 */
export function ordreApresDeplacement(ordre: string[], uid: string, pas: number): string[] {
  const depart = ordre.indexOf(uid);
  if (depart < 0 || pas === 0) return ordre;
  const arrivee = Math.min(ordre.length - 1, Math.max(0, depart + pas));
  if (arrivee === depart) return ordre;
  const suite = ordre.slice();
  suite.splice(depart, 1);
  suite.splice(arrivee, 0, uid);
  return suite;
}

/**
 * De quel cote de la cible le curseur se trouve.
 *
 * L'axe par defaut vient du GENRE (carte en X, champ en Y). Un `data-axis` pose
 * sur l'element l'emporte, exactement comme en V1, ou seule une carte NON pleine
 * largeur portait `data-axis="x"`.
 */
function coteSurvole(element: HTMLElement, genre: GenreDnd, clientX: number, clientY: number): CoteDepot {
  const axe = element.dataset.axis ?? (genre === 'carte' ? 'x' : 'y');
  const boite = element.getBoundingClientRect();
  if (axe === 'x') {
    return clientX > boite.left + boite.width / 2 ? 'apres' : 'avant';
  }
  return clientY > boite.top + boite.height / 2 ? 'apres' : 'avant';
}

/* ========================================================================== */
/* le hook                                                                    */
/* ========================================================================== */

/** L'element survole pendant un glisser, et de quel cote on tombera. */
interface Survol {
  uid: string;
  cote: CoteDepot;
}

/**
 * Le glisser-deposer d'une liste d'`uid`.
 *
 * @param genre    `champ` ou `carte`. Il choisit l'axe de comparaison par
 *                 defaut et les fleches qui deplacent au clavier.
 * @param ordre    l'ordre courant des `uid`, tel que le brief le porte. Il peut
 *                 contenir `FIN_DE_LISTE` : la butee n'existe pas dans le
 *                 modele, l'appelant la retire a l'ecriture.
 * @param onReordonner  appele avec le NOUVEL ordre, jamais avec l'ancien : si
 *                 rien ne bouge, il n'est pas appele du tout.
 *
 * `ordre` et `onReordonner` sont lus dans une ref : `propsDe` ne change donc
 * d'identite que quand l'etat du glisser change, pas a chaque rendu du parent.
 * C'est la meme regle que le `patch` du store, et pour la meme raison : un
 * element qui se remonte pendant la saisie fait sauter le curseur.
 */
export function useDnd(
  genre: GenreDnd,
  ordre: string[],
  onReordonner: (ordre: string[]) => void,
  libelleDe: (uid: string) => string = (uid) => uid,
): Dnd {
  const [enCours, setEnCours] = useState<string | null>(null);
  const [survol, setSurvol] = useState<Survol | null>(null);
  const [annonce, setAnnonce] = useState('');

  const ordreRef = useRef(ordre);
  const onReordonnerRef = useRef(onReordonner);
  const libelleDeRef = useRef(libelleDe);
  useEffect(() => {
    ordreRef.current = ordre;
    onReordonnerRef.current = onReordonner;
    libelleDeRef.current = libelleDe;
  });

  /** L'ordre d'avant la saisie clavier, pour qu'Echap le remette. */
  const ordreAvantSaisie = useRef<string[] | null>(null);

  const appliquer = useCallback((suite: string[]) => {
    if (suite === ordreRef.current) return;
    // La ref avance tout de suite : deux fleches pressees coup sur coup ne
    // doivent pas repartir toutes les deux du meme ordre.
    ordreRef.current = suite;
    onReordonnerRef.current(suite);
    const deplace = enCours;
    if (deplace !== null) {
      setAnnonce(`${libelleDeRef.current(deplace)}, position ${suite.indexOf(deplace) + 1} sur ${suite.length}`);
    }
  }, [enCours]);

  const terminer = useCallback(() => {
    setEnCours(null);
    setSurvol(null);
    ordreAvantSaisie.current = null;
  }, []);

  const propsDe = useCallback(
    (uid: string): PropsDnd => {
      const saisi = enCours === uid;
      const depot = survol && survol.uid === uid && enCours !== null && enCours !== uid ? survol.cote : undefined;

      const ligne: PropsLigne = {
        'data-dnd-genre': genre,
        'data-dnd-uid': uid,
        ...(saisi ? { 'data-dnd-etat': 'saisi' as const } : {}),
        ...(depot ? { 'data-dnd-depot': depot } : {}),

        onDragOver(e) {
          if (enCours === null || enCours === uid) return;
          e.preventDefault();
          e.dataTransfer.dropEffect = 'move';
          const cote = uid === FIN_DE_LISTE ? 'apres' : coteSurvole(e.currentTarget, genre, e.clientX, e.clientY);
          setSurvol((precedent) =>
            precedent && precedent.uid === uid && precedent.cote === cote ? precedent : { uid, cote },
          );
        },

        onDragLeave() {
          setSurvol((precedent) => (precedent && precedent.uid === uid ? null : precedent));
        },

        onDrop(e) {
          if (enCours === null) return;
          e.preventDefault();
          const cote = uid === FIN_DE_LISTE ? 'apres' : coteSurvole(e.currentTarget, genre, e.clientX, e.clientY);
          appliquer(ordreApresDepot(ordreRef.current, enCours, uid, cote));
          terminer();
        },
      };

      const poignee: PropsPoignee = {
        draggable: true,
        tabIndex: 0,
        'aria-roledescription': genre === 'carte' ? 'Carte deplacable' : 'Champ deplacable',
        'aria-grabbed': saisi,
        'aria-label': `Réordonner ${libelleDeRef.current(uid)}`,
        ...(saisi ? { 'data-dnd-etat': 'saisi' as const } : {}),

        onDragStart(e) {
          setEnCours(uid);
          setSurvol(null);
          ordreAvantSaisie.current = null;
          e.dataTransfer.effectAllowed = 'move';
          // V1 posait deja le texte, et certains navigateurs refusent de
          // demarrer un glisser sans donnee. L'echec est sans consequence.
          try {
            e.dataTransfer.setData('text/plain', uid);
          } catch {
            /* rien : le glisser marche quand meme */
          }
        },

        onDragEnd() {
          terminer();
        },

        /**
         * Espace ou Entree saisit, les fleches deplacent d'un cran, Echap
         * annule, Espace ou Entree repose. Tant que rien n'est saisi, les
         * fleches ne font rien : elles restent au navigateur.
         */
        onKeyDown(e) {
          const touche = e.key;

          if (touche === ' ' || touche === 'Spacebar' || touche === 'Enter') {
            e.preventDefault();
            if (saisi) {
              terminer();
              return;
            }
            ordreAvantSaisie.current = ordreRef.current;
            setEnCours(uid);
            setSurvol(null);
            setAnnonce(`${libelleDeRef.current(uid)} saisi, position ${ordreRef.current.indexOf(uid) + 1} sur ${ordreRef.current.length}`);
            return;
          }

          if (!saisi) return;

          if (touche === 'Escape') {
            e.preventDefault();
            if (ordreAvantSaisie.current) appliquer(ordreAvantSaisie.current);
            setAnnonce(`Ordre de ${libelleDeRef.current(uid)} annulé`);
            terminer();
            return;
          }

          // Une carte peut etre cote a cote : les deux axes la deplacent. Un
          // champ est une ligne de tableau, seul le vertical a un sens.
          const versLeDebut = touche === 'ArrowUp' || (genre === 'carte' && touche === 'ArrowLeft');
          const versLaFin = touche === 'ArrowDown' || (genre === 'carte' && touche === 'ArrowRight');
          if (!versLeDebut && !versLaFin) return;
          e.preventDefault();
          appliquer(ordreApresDeplacement(ordreRef.current, uid, versLeDebut ? -1 : 1));
        },

        /** Le focus perdu repose l'element la ou il est : rien ne reste saisi. */
        onBlur() {
          if (saisi) terminer();
        },
      };

      return { ligne, poignee };
    },
    [genre, enCours, survol, appliquer, terminer],
  );

  return useMemo(() => ({ propsDe, enCours, annonce }), [propsDe, enCours, annonce]);
}
